import dayjs from 'dayjs';
import { Company } from '../../../../types/company';
import { Container } from '../../../../types/container';
import { CustomFieldType, CustomFieldUtil } from '../../../../types/custom_field';
import { Product } from '../../../../types/product';
import { ProductMasterData } from '../../../../types/product_master_data/productMasterData';
import { ProductTableSettingsColumn } from '../../../../types/table_settings/product/ProductTableSettings';
import { TableSettings } from '../../../../types/table_settings/TableSettings';
import { TranslationKey } from '../../../../types/translation/TranslationKey';
import { t, translate } from '../../../../types/translation/Translator';
import { UnitOfMeasure } from '../../../../types/uom';
import { toMap } from '../../../../util/map.util';
import { toFilterString } from '../../../../util/string.util';
import { TableHeader } from '../Table';

export function productTableHeaders(
  settings: TableSettings<any>,
  productMasterData: Map<string, ProductMasterData>,
  productQuantity: Map<string, Product>,
  companies: Map<string, Company>,
  company: Company,
  locale: string,
) {
  const wantedColumns = toMap(settings.columns, 'column');

  const columns: TableHeader<string>[] = [];

  const isPartner = company.partner;

  if (isPartner) {
    columns.push({
      key: 'company',
      name: t(TranslationKey.company).singular.label,
      text: (item: string) => companies.get(productMasterData.get(item)?.companyId || '')?.name || '',
    });
  }

  if (wantedColumns.has(ProductTableSettingsColumn.productName)) {
    columns.push({
      key: 'name',
      name:
        wantedColumns.get(ProductTableSettingsColumn.productName)?.label ||
        translate({ key: ProductTableSettingsColumn.productName }),
      text: (item: string) => productMasterData.get(item)?.productName || 'Unknown Product',
      column: ProductTableSettingsColumn.productName,
    });
  }

  if (wantedColumns.has(ProductTableSettingsColumn.productNumber)) {
    columns.push({
      key: 'number',
      name:
        wantedColumns.get(ProductTableSettingsColumn.productNumber)?.label ||
        translate({ key: ProductTableSettingsColumn.productNumber }),
      text: (item: string) => productMasterData.get(item)?.productNumber || 'Unknown Product',
      column: ProductTableSettingsColumn.productNumber,
    });
  }

  if (wantedColumns.has(ProductTableSettingsColumn.quantity)) {
    columns.push({
      key: 'quantity',
      name:
        wantedColumns.get(ProductTableSettingsColumn.quantity)?.label ||
        translate({ key: ProductTableSettingsColumn.quantity }),
      column: ProductTableSettingsColumn.quantity,
      sortValue: (item: string) => productQuantity.get(item)?.unitQuantity || 0,
      text: (item: string) => {
        const pmd = productMasterData.get(item);
        return `${UnitOfMeasure.toQuantityWithSuffix(
          pmd?.unitOfMeasure,
          productQuantity.get(item)?.unitQuantity || '0',
        )}`;
      },
    });
  }

  const defaultKeys = new Set(Object.keys(ProductTableSettingsColumn));
  const customFieldColumns = [...wantedColumns.values()].filter(item => !defaultKeys.has(item.column));

  customFieldColumns.forEach(column => {
    columns.push({
      key: column.column,
      name: column.label,
      column: column.column,
      sortValue: (item: string) => {
        const pmd = productMasterData.get(item);
        if (!pmd) return '';
        const customFieldValue = pmd.customFieldValues().find(i => i.name === column.column);
        if (!customFieldValue) return '';
        if (customFieldValue.type === CustomFieldType.date) return dayjs(customFieldValue.value);
        return CustomFieldUtil.getTextForCustomField(customFieldValue, locale);
      },
      text: (item: string) => {
        const pmd = productMasterData.get(item);
        if (!pmd) return '';
        const customFieldValue = pmd.customFieldValues().find(i => i.name === column.column);
        if (!customFieldValue) return '';
        return CustomFieldUtil.getTextForCustomField(customFieldValue, locale);
      },
    });
  });

  return columns.sort(
    (a: TableHeader<string>, b: TableHeader<string>) =>
      (wantedColumns.get(a.column || '')?.index || -1) - (wantedColumns.get(b.column || '')?.index || -1),
  );
}

export const productTableFilter = (
  item: Product,
  filter: string,
  settings: TableSettings<any>,
  productMasterData: Map<string, ProductMasterData>,
  containers: Map<string, Container>,
) => {
  const filterString = toFilterString(filter);
  if (!filterString) return true;

  const pmd = productMasterData.get(item.productMasterDataId || '');
  if (!pmd) return false;

  const container = item.containerId ? containers.get(item.containerId)?.identifier : undefined;

  const wantedColumns = toMap(settings.columns, 'column');

  if (
    wantedColumns.has(ProductTableSettingsColumn.productName) &&
    toFilterString(pmd.productName).includes(filterString)
  ) {
    return true;
  }

  if (
    wantedColumns.has(ProductTableSettingsColumn.productNumber) &&
    toFilterString(pmd.productNumber).includes(filterString)
  ) {
    return true;
  }

  if (toFilterString(container || '').includes(filterString)) return true;
  const defaultKeys = new Set(Object.keys(ProductTableSettingsColumn));
  const customFieldColumns = [...wantedColumns.values()].filter(c => !defaultKeys.has(c.column));

  for (const custom of customFieldColumns) {
    const field = toFilterString(pmd.customFieldValues().find(i => i.name === custom.column)?.value);
    if (field?.includes(filterString)) return true;
  }

  return false;
};
